'use client'

import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { CheckIcon } from '@/components/svg/check-icon/check-icon'
import { CopyIcon } from '@/components/svg/copy-icon/copy-icon'
import s from './toast-error-enhancer.module.scss'

// sonner's default toast duration is 4000ms; show errors ~50% longer so the
// admin has time to read (and copy) the code before it disappears.
const ERROR_TOAST_DURATION = 6000

// A plain icon button (no sonner action-button chrome) that copies the toast's
// full text — error code and message — and flips to a checkmark as feedback.
const CopyAction = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  useEffect(() => () => clearTimeout(timeoutRef.current), [])

  const handleCopy = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true)
        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {})
  }

  return (
    <button
      type="button"
      className={s.copyButton}
      onClick={handleCopy}
      aria-label="Copy error code and message"
      title={copied ? 'Copied' : 'Copy error'}
    >
      {copied ? (
        <CheckIcon iconColor="currentColor" />
      ) : (
        <CopyIcon iconColor="currentColor" />
      )}
    </button>
  )
}

// Globally augments `toast.error` so every error toast (all ~130 existing call
// sites and any future ones) gets a copy button plus a longer display time.
// Keeps sonner's native error look and its built-in close button; the copy
// action is rendered as a custom element, so it avoids the default (dark)
// action-button styling.
//
// Works by patching the shared `toast` singleton once on the client: callers do
// `toast.error(...)` (a property lookup at call time), so they transparently
// hit the wrapped version. Idempotent and no-op on the server.
export const ToastErrorEnhancer = () => {
  useEffect(() => {
    const patchable = toast as typeof toast & { __errorEnhanced?: boolean }
    if (patchable.__errorEnhanced) return

    const originalError = toast.error.bind(toast)

    patchable.error = ((message, data) => {
      const text = typeof message === 'string' ? message : undefined

      return originalError(message, {
        duration: ERROR_TOAST_DURATION,
        ...data,
        // Preserve an explicit per-call action; otherwise add the copy button.
        action: data?.action ?? (text ? <CopyAction text={text} /> : undefined),
      })
    }) as typeof toast.error

    patchable.__errorEnhanced = true
  }, [])

  return null
}
